<script setup lang="ts">
  import { ref, computed, watch, onMounted, onUnmounted } from "vue";
  import { storeToRefs } from "pinia";
  import { useDebounceFn } from "@vueuse/core";
  import { usePricerProjectStore } from "@stores/pricer/pricerProject";
  import { useHistoStore } from "@stores/histo";
  import { useHistoTable } from "@composables/useHistoTable";
  import { FilterDropdown } from "@/components/episto-ui/filter-dropdown";
  import { DataTableColumnPicker, type ToggleableColumn } from "@/components/episto-ui/data-table";
  import { IndexPageFilters } from "@/components/App/IndexPage";
  import { HistoDataTable } from "@/components/Admin/Histo/HistoDataTable";
  import { HistoDetailSheet } from "@/components/Admin/Histo/HistoDetailSheet";
  import {
    useHistoFilters,
    TOGGLEABLE_COLUMNS,
    HIDDEN_BY_DEFAULT_COLUMNS,
  } from "@/utils/composables/filters/useHistoFilters";
  import type { Histo, HistoTableRow } from "@/components/Admin/Histo/HistoDataTable/types";
  import type { VisibilityState } from "@tanstack/vue-table";

  const pricerProjectStore = usePricerProjectStore();
  const { project, selectedTarget } = storeToRefs(pricerProjectStore);

  const histoStore = useHistoStore();
  const { histos, pagination } = storeToRefs(histoStore);
  const { fetchHistos, resetHistos } = histoStore;
  const { transformHistosToTableRows } = useHistoTable();

  const { filterConfigs, filterValues, searchQuery, clearFilters, buildFiltersPayload, fetchHistoFilters } =
    useHistoFilters({ autoInit: false });

  const targetDescription = computed(() => selectedTarget.value?.description ?? null);
  const surveyTopic = computed(() => project.value?.survey_topic ?? null);

  const isLoading = ref(false);
  const isSheetOpen = ref(false);
  const selectedHisto = ref<Histo | null>(null);
  const columnVisibility = ref<VisibilityState>({
    days: false,
    cpm: false,
    ctr: false,
    cpc: false,
    startRate: false,
    cps: false,
    completionRate: false,
    screenoutRate: false,
    similarity: true,
  });

  const pickerColumns: ToggleableColumn[] = TOGGLEABLE_COLUMNS.map(({ id, label }) => ({
    key: id,
    label,
    hiddenByDefault: HIDDEN_BY_DEFAULT_COLUMNS.includes(id),
  }));

  const tableData = computed<HistoTableRow[]>(() => transformHistosToTableRows(histos.value as Histo[]));

  const paginationInfo = computed(() => ({
    currentPage: pagination.value.currentPage,
    perPage: pagination.value.perPage,
    totalCount: pagination.value.totalCount,
    totalPages: pagination.value.totalPages,
  }));

  const fetchData = async () => {
    isLoading.value = true;
    const filters = buildFiltersPayload();
    await fetchHistos({
      filters,
      target: targetDescription.value,
      topic: surveyTopic.value,
      perPage: 10,
      query: searchQuery.value,
    });
    isLoading.value = false;
  };

  const debouncedFetch = useDebounceFn(fetchData, 600);

  async function handlePageChange(page: number) {
    isLoading.value = true;
    const filters = buildFiltersPayload();
    await fetchHistos({
      filters,
      target: targetDescription.value,
      topic: surveyTopic.value,
      page,
      perPage: 10,
      query: searchQuery.value,
    });
    isLoading.value = false;
  }

  function openDetailSheet(row: HistoTableRow) {
    selectedHisto.value = row.originalHisto;
    isSheetOpen.value = true;
  }

  const isInitialized = ref(false);

  watch(
    [searchQuery, filterValues],
    () => {
      if (isInitialized.value) debouncedFetch();
    },
    { deep: true },
  );

  watch([targetDescription, surveyTopic], () => {
    if (isInitialized.value) fetchData();
  });

  onMounted(async () => {
    await fetchHistoFilters();
    await fetchData();
    isInitialized.value = true;
  });

  onUnmounted(() => resetHistos());
</script>

<template>
  <div>
    <div class="border rounded-lg overflow-hidden">
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Search..."
        class="p-4">
        <template #filters>
          <FilterDropdown
            v-model="filterValues"
            :filters="filterConfigs"
            @clear="clearFilters" />
          <DataTableColumnPicker
            v-model="columnVisibility"
            :columns="pickerColumns" />
        </template>
      </IndexPageFilters>

      <HistoDataTable
        :data="tableData"
        :loading="isLoading"
        :column-visibility="columnVisibility"
        :pagination="paginationInfo"
        @row-click="openDetailSheet"
        @page-change="handlePageChange" />
    </div>

    <HistoDetailSheet
      v-model:open="isSheetOpen"
      :histo="selectedHisto" />
  </div>
</template>
